/**
 * Shared contour extraction and rasterization utilities.
 *
 * Used by:
 * - RegionGrowTool (flood-fill → contour → annotation)
 * - niftiMaskLoader (mask → contour → annotation)
 * - predictionMaskOverlay (AI mask → contour → annotation)
 * - SegmentationStatsOverlay (contour → rasterize → HU stats)
 * - SegmentationAnnotationPanel save flow (contour → rasterize → labelmap)
 */

type Pt2 = [number, number];

// ─── Contour extraction from binary mask ─────────────────────────────────────

/**
 * Extract the outer outline of every blob in a 2D binary mask, traced along
 * pixel EDGES (ENG-250). Blobs are 4-connected by default.
 *
 * Pixel (r, c) covers [r-0.5, r+0.5] × [c-0.5, c+0.5], so every vertex sits on
 * a half-pixel and never on a pixel-centre scan line. `rasterizePolygon` of an
 * outline therefore gives back exactly the blob's pixels (holes filled), with
 * or without `chaikinSmooth(…, 2)`. (Tracing pixel CENTRES, as the old
 * `extractContour` did, dropped the outer half-pixel ring.)
 *
 * - One closed polygon per blob, clockwise on screen (blob on the right).
 * - 4-connected: pixels touching only at a corner are separate polygons; at
 *   such a pinch the walk turns away from the diagonal pixel, so an outline
 *   never touches itself.
 * - 8-connected: the walk goes through the pinch, so a fill that grew
 *   diagonally (Region Grow) gets one outline. It touches itself at the pinch
 *   corner, never crossing; after `chaikinSmooth` the two passes separate.
 * - A vertex at every pixel step: merging straight runs into one long edge lets
 *   Chaikin cut corners wide enough to drop corner pixels.
 *
 * @param mask  Uint8Array of size rows×cols, row-major (mask[r*cols+c])
 * @param cols  Image width
 * @param rows  Image height
 * @param connectivity  4 (sides) or 8 (sides and corners) neighbours per blob
 * @returns One array of [row, col] vertices per blob
 */
export function extractContourEdges(
  mask: Uint8Array,
  cols: number,
  rows: number,
  connectivity: 4 | 8 = 4,
): Pt2[][] {
  const visited = new Uint8Array(rows * cols);
  const queue = new Int32Array(rows * cols);
  const outlines: Pt2[][] = [];

  // Row-major scan: the first unvisited pixel of a blob is its top-left one.
  for (let start = 0; start < rows * cols; start++) {
    if (!mask[start] || visited[start]) continue;

    let head = 0,
      tail = 0;
    queue[tail++] = start;
    visited[start] = 1;
    while (head < tail) {
      const p = queue[head++];
      const r = Math.floor(p / cols),
        c = p - r * cols;
      if (r > 0) tail = enqueue(p - cols, tail);
      if (r < rows - 1) tail = enqueue(p + cols, tail);
      if (c > 0) tail = enqueue(p - 1, tail);
      if (c < cols - 1) tail = enqueue(p + 1, tail);
      if (connectivity === 8) {
        if (r > 0 && c > 0) tail = enqueue(p - cols - 1, tail);
        if (r > 0 && c < cols - 1) tail = enqueue(p - cols + 1, tail);
        if (r < rows - 1 && c > 0) tail = enqueue(p + cols - 1, tail);
        if (r < rows - 1 && c < cols - 1) tail = enqueue(p + cols + 1, tail);
      }
    }

    const r0 = Math.floor(start / cols);
    outlines.push(traceOuterEdge(mask, cols, rows, r0, start - r0 * cols, connectivity === 8));
  }

  return outlines;

  function enqueue(p: number, tail: number): number {
    if (mask[p] && !visited[p]) {
      visited[p] = 1;
      queue[tail++] = p;
    }
    return tail;
  }
}

/**
 * Walk one blob's outer outline. Corner (i, j) is the point (i-0.5, j-0.5).
 * Starts at the top-left corner of the blob's top-left pixel heading right.
 * At each corner, looking ahead: both pixels on → turn left; only the right
 * one → straight; neither → turn right. At a diagonal pinch (only the left
 * one on), 4-connected turns right, away from it; 8-connected turns left,
 * through the corner.
 *
 * Any pixel the walk enters belongs to this blob: 4-connected only enters a
 * left pixel when the right one is on (both share an edge with the blob);
 * 8-connected enters a pinch pixel that touches the blob at that corner.
 */
function traceOuterEdge(
  mask: Uint8Array,
  cols: number,
  rows: number,
  r0: number,
  c0: number,
  throughCorners: boolean,
): Pt2[] {
  // Directions: 0 right, 1 down, 2 left, 3 up.
  const di = [0, 1, 0, -1],
    dj = [1, 0, -1, 0];
  const on = (r: number, c: number) =>
    r >= 0 && r < rows && c >= 0 && c < cols && mask[r * cols + c] !== 0;

  const outline: Pt2[] = [];
  let i = r0,
    j = c0,
    d = 0;
  do {
    outline.push([i - 0.5, j - 0.5]);
    i += di[d];
    j += dj[d];
    // Pixels ahead of corner (i, j), to the right and left of heading d.
    let right: boolean, left: boolean;
    if (d === 0) {
      right = on(i, j);
      left = on(i - 1, j);
    } else if (d === 1) {
      right = on(i, j - 1);
      left = on(i, j);
    } else if (d === 2) {
      right = on(i - 1, j - 1);
      left = on(i, j - 1);
    } else {
      right = on(i - 1, j);
      left = on(i - 1, j - 1);
    }
    if (left && (right || throughCorners)) d = (d + 3) % 4;
    else if (!right) d = (d + 1) % 4;
  } while (i !== r0 || j !== c0 || d !== 0);

  return outline;
}

// ─── Chaikin smoothing ───────────────────────────────────────────────────────

/**
 * Chaikin corner-cutting subdivision for closed contours.
 * Each iteration replaces each edge with two points at 25%/75%.
 */
export function chaikinSmooth(points: Pt2[], iterations: number): Pt2[] {
  let pts = points;
  for (let iter = 0; iter < iterations; iter++) {
    const n = pts.length;
    if (n < 3) return pts;
    const smoothed: Pt2[] = [];
    for (let i = 0; i < n; i++) {
      const [r1, c1] = pts[i];
      const [r2, c2] = pts[(i + 1) % n];
      smoothed.push([r1 * 0.75 + r2 * 0.25, c1 * 0.75 + c2 * 0.25]);
      smoothed.push([r1 * 0.25 + r2 * 0.75, c1 * 0.25 + c2 * 0.75]);
    }
    pts = smoothed;
  }
  return pts;
}

// ─── World → pixel ───────────────────────────────────────────────────────────

/**
 * Map a world point to continuous [row, col] pixel coordinates (ENG-250).
 *
 * Do not round: Cornerstone's `transformWorldToIndex` rounds to whole pixels,
 * which pulls the ring of pixels under a Lasso stroke into the mask and
 * inflates small stones. `rasterizePolygon` keeps exactly the pixels whose
 * centres lie inside the float polygon.
 *
 * @param imageData  vtkImageData of the viewport (`worldToIndex` → [col, row, slice])
 */
export function worldToPixel(
  imageData: { worldToIndex(point: number[]): ArrayLike<number> },
  worldPoint: number[],
): Pt2 {
  const index = imageData.worldToIndex(worldPoint);
  return [index[1], index[0]];
}

// ─── Scan-line polygon rasterization ─────────────────────────────────────────

/**
 * Rasterize a closed polygon into pixel indices using scan-line fill.
 *
 * @param points  Array of [row, col] polygon vertices
 * @param cols    Image width
 * @param rows    Image height
 * @returns Array of flat pixel indices (row * cols + col) inside the polygon
 */
export function rasterizePolygon(points: Pt2[], cols: number, rows: number): number[] {
  if (points.length < 3) return [];

  let minR = Infinity,
    maxR = -Infinity;
  for (const [r] of points) {
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
  }
  minR = Math.max(0, Math.floor(minR));
  maxR = Math.min(rows - 1, Math.ceil(maxR));

  const indices: number[] = [];
  const n = points.length;

  for (let r = minR; r <= maxR; r++) {
    const intersections: number[] = [];
    for (let i = 0; i < n; i++) {
      const [r1, c1] = points[i];
      const [r2, c2] = points[(i + 1) % n];
      if ((r1 <= r && r2 > r) || (r2 <= r && r1 > r)) {
        const t = (r - r1) / (r2 - r1);
        intersections.push(c1 + t * (c2 - c1));
      }
    }
    intersections.sort((a, b) => a - b);
    for (let i = 0; i < intersections.length - 1; i += 2) {
      const cStart = Math.max(0, Math.ceil(intersections[i]));
      const cEnd = Math.min(cols - 1, Math.floor(intersections[i + 1]));
      for (let c = cStart; c <= cEnd; c++) {
        indices.push(r * cols + c);
      }
    }
  }

  return indices;
}
